import React from 'react';
import { useI18n } from '../contexts/I18nContext';
import RichTextEditor from './RichTextEditor';
import './PropertiesEditor.css';

// PropertiesEditor edits an arbitrary, typed key/value map (the image's custom
// "properties"). It works on an internal *model* (ordered entries with stable
// ids, so editing a key never remounts inputs) and serialises to/from the plain
// JSON object stored in the DB and shipped in the SVG's data-meta.properties.
//
// Value shape, both in the editor model and the serialised JSON, is a recursive
// "node": { type, value, itemType? }. Containers nest more nodes:
//   record / typed_record → JSON object  { key: node }   (model: [{id,key,node}])
//   array  / typed_array  → JSON array   [ node ]        (model: [{id,node}])
// "typed_*" variants pick one item type for every child (children render no type
// chooser); the plain variants let each child choose its own type.

// The full ordered list of selectable value types.
export const VALUE_TYPES = [
  'richtext', 'text', 'longtext', 'boolean',
  'int', 'uint', 'decimal',
  'date', 'time', 'datetime', 'timetz', 'datetimetz', 'duration',
  'array', 'typed_array', 'record', 'typed_record',
];

const CONTAINER_TYPES = new Set(['array', 'typed_array', 'record', 'typed_record']);
const TYPED_TYPES = new Set(['typed_array', 'typed_record']);

// Native <input type> per scalar type; everything else falls back to a text box.
const NATIVE_INPUT = { date: 'date', time: 'time', datetime: 'datetime-local' };
// Hint shown for free-text temporal/duration values without a native picker.
const TEXT_PLACEHOLDER = {
  duration: 'PT1H30M',
  timetz: 'HH:MM:SS±HH:MM',
  datetimetz: 'YYYY-MM-DDTHH:MM:SS±HH:MM',
};

let _idCounter = 0;
const uid = () => `p${++_idCounter}_${Math.random().toString(36).slice(2, 7)}`;

// modelDefault returns the empty model value for a type. Containers hold an
// (ordered) array of child entries/items; scalars hold their primitive zero.
function modelDefault(type) {
  switch (type) {
    case 'boolean': return false;
    case 'int': case 'uint': case 'decimal': return 0;
    case 'array': case 'typed_array': case 'record': case 'typed_record': return [];
    default: return ''; // richtext, text, longtext, date, time, datetime, timetz, datetimetz, duration
  }
}

// makeNode builds a fresh model node of the given type (typed containers default
// their child type to 'text').
export function makeNode(type = 'text') {
  const node = { type, value: modelDefault(type) };
  if (TYPED_TYPES.has(type)) node.itemType = 'text';
  return node;
}

// coerceNumber parses a numeric input string for int/uint/decimal, clamping
// uint at 0 and truncating the integer types. Invalid input becomes 0.
export function coerceNumber(type, raw) {
  if (raw === '' || raw == null) return 0;
  let n = type === 'decimal' ? parseFloat(raw) : parseInt(raw, 10);
  if (Number.isNaN(n)) return 0;
  if (type !== 'decimal') n = Math.trunc(n);
  if (type === 'uint' && n < 0) n = 0;
  return n;
}

// ── model ⇆ JSON ───────────────────────────────────────────────────────────────

const isPlainObject = (v) => v != null && typeof v === 'object' && !Array.isArray(v);

// nodeToModel converts a serialised JSON node ({ type, value, itemType? }) into
// an editor model node, assigning stable ids to container children. Used for the
// top level and for *plain* array/record children (which carry their own type).
export function nodeToModel(json) {
  const type = (json && json.type) || 'text';
  return valueToModelNode(type, json ? json.value : undefined, json && json.itemType);
}

// valueToModelNode builds a model node of `type` from a *bare* value (no type
// wrapper) — the form typed containers store their items in, where the item type
// is fixed by the container's itemType rather than repeated on every item.
// Tolerates malformed input. Note: when itemType is itself a typed container the
// nested itemType isn't carried by a bare value, so it defaults to 'text'.
function valueToModelNode(type, value, itemType) {
  if (type === 'record') {
    const obj = isPlainObject(value) ? value : {};
    return { type, value: Object.keys(obj).map(k => ({ id: uid(), key: k, node: nodeToModel(obj[k]) })) };
  }
  if (type === 'typed_record') {
    const it = itemType || 'text';
    const obj = isPlainObject(value) ? value : {};
    return { type, itemType: it, value: Object.keys(obj).map(k => ({ id: uid(), key: k, node: valueToModelNode(it, obj[k]) })) };
  }
  if (type === 'array') {
    const arr = Array.isArray(value) ? value : [];
    return { type, value: arr.map(v => ({ id: uid(), node: nodeToModel(v) })) };
  }
  if (type === 'typed_array') {
    const it = itemType || 'text';
    const arr = Array.isArray(value) ? value : [];
    return { type, itemType: it, value: arr.map(v => ({ id: uid(), node: valueToModelNode(it, v) })) };
  }
  return { type, value: value === undefined ? modelDefault(type) : value };
}

// nodeToJSON serialises a model node to its full { type, value, itemType? } JSON
// shape. Record/array children keep their own type wrapper; typed_record/
// typed_array children are serialised as *bare* values (no per-item type key).
// Record entries with a blank key are dropped.
export function nodeToJSON(node) {
  const { type, itemType } = node;
  if (type === 'record') {
    const obj = {};
    for (const e of node.value || []) if (e.key) obj[e.key] = nodeToJSON(e.node);
    return { type, value: obj };
  }
  if (type === 'typed_record') {
    const obj = {};
    for (const e of node.value || []) if (e.key) obj[e.key] = nodeToBareValue(e.node);
    return { type, itemType, value: obj };
  }
  if (type === 'array') {
    return { type, value: (node.value || []).map(it => nodeToJSON(it.node)) };
  }
  if (type === 'typed_array') {
    return { type, itemType, value: (node.value || []).map(it => nodeToBareValue(it.node)) };
  }
  return { type, value: node.value };
}

// nodeToBareValue serialises just a node's value (no type wrapper) — what typed
// containers store for each item. Plain array/record children inside keep their
// type wrapper; typed children stay bare, recursively.
function nodeToBareValue(node) {
  const { type } = node;
  if (type === 'record') {
    const obj = {};
    for (const e of node.value || []) if (e.key) obj[e.key] = nodeToJSON(e.node);
    return obj;
  }
  if (type === 'typed_record') {
    const obj = {};
    for (const e of node.value || []) if (e.key) obj[e.key] = nodeToBareValue(e.node);
    return obj;
  }
  if (type === 'array') {
    return (node.value || []).map(it => nodeToJSON(it.node));
  }
  if (type === 'typed_array') {
    return (node.value || []).map(it => nodeToBareValue(it.node));
  }
  return node.value;
}

// propsToModel / propsToJSON bridge the top-level properties object (the record
// at the root) and the editor's ordered-entries model.
export function propsToModel(obj) {
  const o = isPlainObject(obj) ? obj : {};
  return Object.keys(o).map(k => ({ id: uid(), key: k, node: nodeToModel(o[k]) }));
}

export function propsToJSON(entries) {
  const obj = {};
  for (const e of entries || []) {
    if (e.key) obj[e.key] = nodeToJSON(e.node);
  }
  return obj;
}

// ── import: raw JSON → model ─────────────────────────────────────────────────────

// inferNode builds a model node from a raw JS value, picking a type from its
// runtime shape (bool→boolean, integer→int, fractional→decimal, array→array,
// object→record, everything else→text). Used when importing untyped JSON.
export function inferNode(value) {
  if (typeof value === 'boolean') return { type: 'boolean', value };
  if (typeof value === 'number') return { type: Number.isInteger(value) ? 'int' : 'decimal', value };
  if (Array.isArray(value)) return { type: 'array', value: value.map(v => ({ id: uid(), node: inferNode(v) })) };
  if (isPlainObject(value)) {
    return { type: 'record', value: Object.keys(value).map(k => ({ id: uid(), key: k, node: inferNode(value[k]) })) };
  }
  return { type: 'text', value: value == null ? '' : String(value) };
}

// coerceScalar nudges a raw value into a scalar type for import (numbers/bools
// parsed, everything else stringified).
function coerceScalar(type, value) {
  if (type === 'boolean') return value === true || value === 'true';
  if (type === 'int' || type === 'uint' || type === 'decimal') return coerceNumber(type, value);
  if (value == null) return '';
  return typeof value === 'string' ? value : String(value);
}

// importNode builds a model node of a *known* type from a raw value (used for
// typed containers, whose children share one type). With no type it infers.
// A container itemType that is itself a typed container can't carry its nested
// itemType through raw JSON, so that nested type defaults to 'text'.
export function importNode(value, type) {
  if (!type) return inferNode(value);
  if (type === 'record') {
    const obj = isPlainObject(value) ? value : {};
    return { type, value: Object.keys(obj).map(k => ({ id: uid(), key: k, node: inferNode(obj[k]) })) };
  }
  if (type === 'array') {
    const arr = Array.isArray(value) ? value : [];
    return { type, value: arr.map(v => ({ id: uid(), node: inferNode(v) })) };
  }
  if (type === 'typed_record') {
    const obj = isPlainObject(value) ? value : {};
    return { type, itemType: 'text', value: Object.keys(obj).map(k => ({ id: uid(), key: k, node: importNode(obj[k], 'text') })) };
  }
  if (type === 'typed_array') {
    const arr = Array.isArray(value) ? value : [];
    return { type, itemType: 'text', value: arr.map(v => ({ id: uid(), node: importNode(v, 'text') })) };
  }
  return { type, value: coerceScalar(type, value) };
}

// mergeRecordEntries merges a plain object into record entries: matching keys are
// overwritten, new keys appended (order preserved). `itemType` (typed_record)
// coerces every value to that type; otherwise each value's type is inferred.
export function mergeRecordEntries(entries, obj, itemType) {
  if (!isPlainObject(obj)) return entries || [];
  const next = (entries || []).slice();
  const pos = new Map(next.map((e, i) => [e.key, i]));
  for (const [k, v] of Object.entries(obj)) {
    const node = importNode(v, itemType);
    if (pos.has(k)) next[pos.get(k)] = { ...next[pos.get(k)], node };
    else next.push({ id: uid(), key: k, node });
  }
  return next;
}

// mergeArrayItems appends parsed array elements as new items. `itemType`
// (typed_array) coerces each element; otherwise each element's type is inferred.
export function mergeArrayItems(items, arr, itemType) {
  if (!Array.isArray(arr)) return items || [];
  return (items || []).concat(arr.map(v => ({ id: uid(), node: importNode(v, itemType) })));
}

// ── components ──────────────────────────────────────────────────────────────────

// JsonImport renders a small "Import JSON" control: a button that reveals a
// textarea + Merge, parsing the pasted JSON and handing the parsed value to
// onImport (no-op with an inline error on invalid JSON). Reused for every
// container value and for the top-level properties field.
export function JsonImport({ onImport }) {
  const { t } = useI18n();
  const [open, setOpen] = React.useState(false);
  const [text, setText] = React.useState('');
  const [err, setErr] = React.useState('');
  const close = () => { setOpen(false); setText(''); setErr(''); };
  const submit = () => {
    let parsed;
    try { parsed = JSON.parse(text); } catch { setErr(t('prop.import_error')); return; }
    onImport(parsed);
    close();
  };
  if (!open) {
    return (
      <button type="button" className="btn btn-secondary btn-sm prop-import-btn"
        onClick={() => setOpen(true)}>{t('prop.import')}</button>
    );
  }
  return (
    <div className="prop-import">
      <textarea className="prop-input" rows={3} value={text} aria-label={t('prop.import')}
        placeholder={t('prop.import_placeholder')}
        onChange={e => { setText(e.target.value); setErr(''); }} />
      {err && <div className="prop-import-error">{err}</div>}
      <div className="prop-import-actions">
        <button type="button" className="btn btn-secondary btn-sm" onClick={close}>{t('common.cancel')}</button>
        <button type="button" className="btn btn-primary btn-sm" onClick={submit}>{t('prop.import_merge')}</button>
      </div>
    </div>
  );
}

function TypeSelect({ value, onChange, ariaLabel }) {
  const { t } = useI18n();
  return (
    <select className="prop-type-select" aria-label={ariaLabel} value={value}
      onChange={e => onChange(e.target.value)}>
      {VALUE_TYPES.map(ty => <option key={ty} value={ty}>{t('prop.type.' + ty)}</option>)}
    </select>
  );
}

function ArrayEditor({ items, onChange, lockType }) {
  const { t } = useI18n();
  const add = () => onChange([...(items || []), { id: uid(), node: makeNode(lockType || 'text') }]);
  const update = (id, node) => onChange(items.map(it => (it.id === id ? { ...it, node } : it)));
  const remove = (id) => onChange(items.filter(it => it.id !== id));
  return (
    <div className="prop-array">
      {(items || []).map(it => (
        <div className="prop-array-item" key={it.id}>
          <PropertyValueEditor node={it.node} lockType={lockType} onChange={n => update(it.id, n)} />
          <button type="button" className="btn btn-secondary btn-sm prop-remove"
            aria-label={t('prop.remove')} onClick={() => remove(it.id)}>✕</button>
        </div>
      ))}
      <button type="button" className="btn btn-secondary btn-sm" onClick={add}>{t('prop.add_item')}</button>
    </div>
  );
}

// PropertyValueEditor renders one value: a type chooser (unless lockType pins it)
// plus the input appropriate to the effective type. Containers recurse.
export function PropertyValueEditor({ node, onChange, lockType }) {
  const { t } = useI18n();
  const type = lockType || node.type;
  const setValue = (value) => onChange({ ...node, type, value });
  const changeType = (nt) => onChange(makeNode(nt));
  // Re-typing children of a typed container resets them (their value shape changes).
  const setItemType = (it) => onChange({ ...node, type, itemType: it, value: [] });

  let valueEl;
  switch (type) {
    case 'richtext':
      valueEl = <RichTextEditor value={node.value || ''} onChange={setValue} />;
      break;
    case 'longtext':
      valueEl = <textarea className="prop-input" rows={3} value={node.value || ''}
        onChange={e => setValue(e.target.value)} />;
      break;
    case 'boolean':
      valueEl = <input type="checkbox" checked={!!node.value} onChange={e => setValue(e.target.checked)} />;
      break;
    case 'int': case 'uint': case 'decimal':
      valueEl = <input type="number" className="prop-input"
        step={type === 'decimal' ? 'any' : '1'} min={type === 'uint' ? 0 : undefined}
        value={node.value ?? 0} onChange={e => setValue(coerceNumber(type, e.target.value))} />;
      break;
    case 'date': case 'time': case 'datetime':
      valueEl = <input type={NATIVE_INPUT[type]} className="prop-input"
        step={type === 'date' ? undefined : 1} value={node.value || ''}
        onChange={e => setValue(e.target.value)} />;
      break;
    case 'array':
      valueEl = (
        <>
          <ArrayEditor items={node.value || []} onChange={setValue} />
          <JsonImport onImport={p => setValue(mergeArrayItems(node.value || [], p))} />
        </>
      );
      break;
    case 'record':
      valueEl = (
        <>
          <PropertiesEditor entries={node.value || []} onChange={setValue} />
          <JsonImport onImport={p => setValue(mergeRecordEntries(node.value || [], p))} />
        </>
      );
      break;
    case 'typed_array':
      valueEl = (
        <div className="prop-typed">
          <label className="prop-itemtype">{t('prop.item_type')}
            <TypeSelect value={node.itemType || 'text'} onChange={setItemType} ariaLabel={t('prop.item_type')} />
          </label>
          <ArrayEditor items={node.value || []} lockType={node.itemType || 'text'}
            onChange={v => onChange({ ...node, type, value: v })} />
          <JsonImport onImport={p => setValue(mergeArrayItems(node.value || [], p, node.itemType || 'text'))} />
        </div>
      );
      break;
    case 'typed_record':
      valueEl = (
        <div className="prop-typed">
          <label className="prop-itemtype">{t('prop.item_type')}
            <TypeSelect value={node.itemType || 'text'} onChange={setItemType} ariaLabel={t('prop.item_type')} />
          </label>
          <PropertiesEditor entries={node.value || []} lockType={node.itemType || 'text'}
            onChange={v => onChange({ ...node, type, value: v })} />
          <JsonImport onImport={p => setValue(mergeRecordEntries(node.value || [], p, node.itemType || 'text'))} />
        </div>
      );
      break;
    default: // text, duration, timetz, datetimetz
      valueEl = <input type="text" className="prop-input" placeholder={TEXT_PLACEHOLDER[type] || ''}
        value={node.value || ''} onChange={e => setValue(e.target.value)} />;
  }

  return (
    <div className={`prop-value${CONTAINER_TYPES.has(type) ? ' prop-value-container' : ''}`}>
      {!lockType && <TypeSelect value={node.type} onChange={changeType} ariaLabel={t('prop.value_type')} />}
      <div className="prop-value-input">{valueEl}</div>
    </div>
  );
}

// PropertiesEditor edits an ordered list of { id, key, node } entries (a record).
// `lockType` pins every value to one type (used by typed_record), hiding the
// per-value type chooser.
export function PropertiesEditor({ entries, onChange, lockType }) {
  const { t } = useI18n();
  const update = (id, patch) => onChange((entries || []).map(e => (e.id === id ? { ...e, ...patch } : e)));
  const remove = (id) => onChange((entries || []).filter(e => e.id !== id));
  const add = () => onChange([...(entries || []), { id: uid(), key: '', node: makeNode(lockType || 'text') }]);

  return (
    <div className="prop-editor">
      {(entries || []).map(e => (
        <div className="prop-row" key={e.id}>
          <input className="prop-key" placeholder={t('prop.key')} value={e.key}
            onChange={ev => update(e.id, { key: ev.target.value })} />
          <PropertyValueEditor node={e.node} lockType={lockType} onChange={n => update(e.id, { node: n })} />
          <button type="button" className="btn btn-secondary btn-sm prop-remove"
            aria-label={t('prop.remove')} onClick={() => remove(e.id)}>✕</button>
        </div>
      ))}
      <button type="button" className="btn btn-secondary btn-sm prop-add" onClick={add}>{t('prop.add')}</button>
    </div>
  );
}

export default PropertiesEditor;
